import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Brain, Target, Users, Zap, Globe, Heart, Play, Search, Sparkles, Eye, FileText, Users as UsersIcon, Clock, BarChart3 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useParallax } from '@/hooks/useParallax';
import DocumentTitle from '@/components/DocumentTitle';

const Features = () => {
  const { user } = useAuth();
  
  // Refs for parallax elements
  const starsRef = useRef<HTMLDivElement>(null);
  const planet1Ref = useRef<HTMLDivElement>(null);
  const planet2Ref = useRef<HTMLDivElement>(null);
  
  // Parallax styles
  const starsStyle = useParallax(starsRef, { speed: 0.05 });
  const planet1Style = useParallax(planet1Ref, { speed: -0.1 });
  const planet2Style = useParallax(planet2Ref, { speed: -0.15 });

  const features = [
    {
      icon: <Search className="h-8 w-8" />,
      title: "Semantic Video Search",
      description: "Find specific moments in your videos using natural language queries. No more scrubbing through hours of content."
    },
    {
      icon: <Eye className="h-8 w-8" />,
      title: "Visual Object Detection",
      description: "Automatically detect and track objects, people, and scenes throughout your video content."
    },
    {
      icon: <FileText className="h-8 w-8" />,
      title: "AI-Powered Transcription",
      description: "Generate accurate transcripts with speaker identification and timestamps for easy navigation."
    },
    {
      icon: <UsersIcon className="h-8 w-8" />,
      title: "Speaker Diarization",
      description: "Identify and track different speakers in your videos for better content organization."
    },
    {
      icon: <Clock className="h-8 w-8" />,
      title: "Smart Timestamping",
      description: "Get precise timestamps for every important moment, making content repurposing effortless."
    },
    {
      icon: <BarChart3 className="h-8 w-8" />,
      title: "Content Analytics",
      description: "Gain insights into your video content with detailed analytics and performance metrics."
    }
  ];

  const benefits = [
    {
      icon: <Clock className="h-6 w-6" />,
      title: "Save 90% of Time",
      description: "Stop scrubbing through hours of footage. Find what you need in seconds."
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: "Increase Accuracy",
      description: "AI-powered analysis catches details human reviewers might miss."
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Scale Efficiently",
      description: "Process thousands of hours of content with the same effort as one video."
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: "Universal Access",
      description: "Works with any video format, from any source, anywhere in the world."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-900 to-purple-900 text-white relative overflow-hidden">
      <DocumentTitle 
        title="Features - VideoBrain.ai | Advanced Video Intelligence Tools"
        description="Explore VideoBrain.ai's powerful features including semantic search, object detection, AI transcription, speaker diarization, and content analytics."
      />
      
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-md bg-black/20 sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-pink-600 flex items-center justify-center relative">
              <Brain className="h-6 w-6 text-white" />
              <div className="absolute inset-0 rounded-full bg-pink-600 animate-pulse opacity-50"></div>
            </div>
            <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-purple-400">VideoBrain</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/features" className="text-white font-semibold">Features</Link>
            <Link to="/pricing" className="text-gray-300 hover:text-white transition-colors">Pricing</Link>
            <Link to="/roadmap" className="text-gray-300 hover:text-white transition-colors">Roadmap</Link>
            <Link to="/about" className="text-gray-300 hover:text-white transition-colors">About</Link>
          </nav>
          <div className="flex items-center gap-4">
            {user ? (
              <Button asChild className="bg-pink-600 hover:bg-pink-700">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <Button asChild className="bg-pink-600 hover:bg-pink-700">
                <Link to="/login">Get Started</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Parallax background elements */}
      <div ref={starsRef} className="absolute inset-0 pointer-events-none" style={starsStyle}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIgLz48L3N2Zz4=')]"></div>
        
        {/* Stars */}
        <div className="stars absolute inset-0">
          {[...Array(100)].map((_, i) => (
            <div 
              key={i} 
              className="absolute rounded-full bg-white" 
              style={{
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.7 + 0.3,
                animation: `twinkle ${Math.random() * 5 + 3}s infinite ${Math.random() * 5}s`
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Floating planets */}
      <div 
        ref={planet1Ref}
        className="absolute -right-32 top-1/4 w-64 h-64 rounded-full bg-gradient-to-br from-purple-300/20 to-indigo-600/20 blur-xl"
        style={planet1Style}
      ></div>
      
      <div 
        ref={planet2Ref}
        className="absolute -left-20 top-2/3 w-40 h-40 rounded-full bg-gradient-to-br from-pink-500/20 to-red-500/10 blur-lg"
        style={planet2Style}
      ></div>

      {/* Hero section */}
      <section className="py-20 relative z-10">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-300">
              Powerful Video Intelligence Features
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto">
              Discover how VideoBrain transforms your video content with cutting-edge AI technology
            </p>
          </div>
        </div>
      </section>

      {/* Main Features Grid */}
      <section className="py-20 relative z-10">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-pink-500/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl"
              >
                <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center mb-6 text-white">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                <p className="text-gray-300 mb-4 text-lg">{feature.description}</p>
                <div className="bg-black/30 rounded-lg p-4 border border-pink-500/20">
                  <p className="text-sm text-pink-200">{feature.demo}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-black/30 backdrop-blur-sm relative z-10">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-purple-400">
              Why Choose VideoBrain?
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="h-12 w-12 rounded-lg bg-pink-600/20 flex items-center justify-center mx-auto mb-4 text-pink-400">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                <p className="text-gray-300">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section className="py-20 relative z-10">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Video Workflow?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of creators and businesses using VideoBrain to unlock the power of their video content.
            </p>
            
            <Button size="lg" asChild className="bg-pink-600 hover:bg-pink-700 px-8 py-6 text-lg group transition-all duration-300">
              {user ? 
                <Link to="/dashboard" className="flex items-center">
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link> : 
                <Link to="/login" className="flex items-center">
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              }
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/40 backdrop-blur-md py-12 border-t border-white/10">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-6 md:mb-0">
              <div className="rounded-md bg-pink-600 p-1">
                <div className="h-6 w-6 text-white flex items-center justify-center font-bold">VB</div>
              </div>
              <span className="font-semibold text-xl">VideoBrain.ai</span>
            </div>
            <div className="flex gap-6">
              <Link to="/features" className="text-white font-semibold">Features</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-white transition-colors">Pricing</Link>
              <Link to="/roadmap" className="text-gray-300 hover:text-white transition-colors">Roadmap</Link>
              <Link to="/about" className="text-gray-300 hover:text-white transition-colors">About</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS for animations */}
      <style>
      {`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `}
      </style>
    </div>
  );
};

export default Features;
